import React, { PureComponent } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import styles from "./SalesChange.module.scss";
import { Stack } from "@mui/material";
import Dropdown from "../../core/Dropdown";
import { getSalesData } from "../../../apis/dashboard";
import { Option } from "../../../libs/types";
import { getUserOptions } from "../../../apis/brand-api";
import { UserResponseData } from "../../../apis/models/BrandlistResponse";
import { formatNumber } from "../../../libs/helpers";

interface InvoiceTotalPriceInterface {
  current: { year: number; month: number; total_price_calc: number;}[];
  previous: { year: number; month: number; total_price_calc: number;}[];
}

interface ChartDataType {
  month: string;
  currentPrice: number;
  previousPrice: number;
}

const getCurrentNendo = () => {
  const now = new Date();
  return now.getMonth() > 8 ? now.getFullYear() + 1 : now.getFullYear();
}

const createYearOptions = () => {
  const choices: Option[] = [];
  const minYear = 2022;
  const maxYear = getCurrentNendo();
  for (let i = maxYear; i >= minYear; i--) {
    choices.push({ name: String(i), value: i});
  }

  return choices;
};

class CustomizedAxisTick extends PureComponent<{x?: any, y?:any, payload?: any, index?: number, year: number}> {
  render() {
    const { x, y, payload } = this.props;

    return (
      <g transform={`translate(${x},${y})`}>
        <text x={-10} y={8} dy={0} fill="#666" fontSize={12}>
          {payload.value}
        </text>
        {this.props.index === 0 &&
          <text x={-10} y={23} dy={0} fill="#666" fontSize={11}>
            {this.props.year - 1}年
          </text>
        }
        {this.props.index === 11 &&
          <text x={-10} y={23} dy={0} fill="#666" fontSize={11}>
            {this.props.year}年
          </text>
        }
      </g>
    );
  }
}

const X_AXIS = [10, 11, 12, 1, 2, 3, 4, 5, 6, 7, 8, 9];

const yFormatter = (value: any, index: number) => {
  return formatNumber(value)
}

const SalesChangeGraph = () => {
  const [changeOfSalesYear, setChangeOfSalesYear] = React.useState<number>(getCurrentNendo());
  const [changeOfSalesStaff, setChangeOfSalesStaff] = React.useState<number>(0);
  const [apiData, setApiData] = React.useState<InvoiceTotalPriceInterface>();
  const [staffData, setStaffData] = React.useState<UserResponseData[]>([]);

  React.useEffect(() => {
    let isActive = true;

    getUserOptions()
      .then(res => {
        if (isActive) {
          const userList: UserResponseData[] = res.data.data;
          setStaffData(userList);
        }
      })
      .catch(err => console.log(err))

    getSalesData(getCurrentNendo())
      .then((res) => {
        if (isActive) {
          setApiData(res.data.data);
        }
      })
      .catch((err) => console.log(err));
    return () => {
      isActive = false;
    }
  }, []);

  const staffList = React.useMemo(() => {
    const results: Option[] = [{name: "　", value: 0}];
    staffData.forEach(staff => results.push({ name: staff.userName, value: staff.aid } as Option));
    return results;
  }, [staffData]);

  const chartData = React.useMemo(() => {
    return X_AXIS.map(month => {
      const currentPrice = apiData?.current.find(data => data.month === month)
      const previousPrice = apiData?.previous.find(data => data.month === month)
      return {
        month: String(month).padStart(2, '0') + "月",
        currentPrice: currentPrice ? currentPrice.total_price_calc : 0,
        previousPrice: previousPrice ? previousPrice.total_price_calc : 0,
      } as ChartDataType
    })
  }, [apiData]);

  async function onChangeNendo (nendo: number) {
    setChangeOfSalesYear(nendo)
    const response = await getSalesData(nendo, changeOfSalesStaff > 0 ? String(changeOfSalesStaff) : undefined)
    setApiData(response.data.data)
  }

  async function onChangeStaff (staffId: number) {
    setChangeOfSalesStaff(staffId)
    const response = await getSalesData(changeOfSalesYear, staffId > 0 ? String(staffId) : undefined)
    setApiData(response.data.data)
  }

  return (
    <div className={styles.SalesChartContainer}>
      <div className={styles.SalesChartContent}>
        <div className={styles.title}>
          <h2>売上推移</h2>
        </div>
        <div className={styles.body}>
          <Stack spacing={2} style={{width: "180px"}}>
            <Dropdown
              options={createYearOptions()}
              value={changeOfSalesYear}
              label="年度"
              onChange={(val) => onChangeNendo(val)}
            />
            <Dropdown
              options={staffList}
              value={changeOfSalesStaff}
              label="担当者名"
              onChange={(val) => onChangeStaff(val)}
            />
          </Stack>
          <ResponsiveContainer width={"90%"} height={350}>
            <LineChart
              data={chartData}
              margin={{top: 5, right: 10, left: 40, bottom: 5}}
            >
              <CartesianGrid stroke="#ccc" />
              <XAxis fontSize={12} dataKey="month" tick={<CustomizedAxisTick year={changeOfSalesYear} />} />
              <YAxis fontSize={12} tickFormatter={yFormatter}/>
              <Tooltip />
              <Legend
                iconType="square"
                layout="vertical"
                verticalAlign="top"
                align="right"
                wrapperStyle={{paddingLeft: "20px"}}
              />
              <Line
                type="linear"
                dataKey={"currentPrice"}
                stroke="#75A857"
                activeDot={{ r: 8 }}
                dot={{ strokeWidth: 1 }}
                name={`${changeOfSalesYear}年度`}
                animationDuration={500}
              />
              <Line
                type="linear"
                dataKey={"previousPrice"}
                stroke="#1E53A3"
                activeDot={{ r: 8 }}
                dot={{ strokeWidth: 1 }}
                name={`${changeOfSalesYear - 1}年度`}
                animationDuration={500}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default SalesChangeGraph;
